import type Luminary from '@lumen5/luminary';
import type { OpaqueVideoData } from '@lumen5/luminary';

import type { BoundingBox, SceneAttributesResponse } from 'js/src/client';
import { ApiService } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import { clearTrackedSlideTextAssets, getSwapSceneDesignSlideId } from 'js/src/data/scenes';
import type { getTemplateBundle } from 'js/src/data/templates/templates-selectors';
import type { SceneDesignId } from 'js/src/data/templates/templates-types';
import { $getFineOverlayBoundaryTimeCloseTo } from 'js/src/data/transcript';
import { getEditorFromSceneGroupId } from 'js/src/data/transcript/transcript-selectors';
import { $getWordNodesBetweenTimes } from 'js/src/data/transcript/transcript-utils';
import { checkpointVideoOperation } from 'js/src/data/video-data';
import SceneSwapper from 'js/src/libs/scene-swapping/SceneSwapper';
import uuid from 'js/src/libs/uuid';
import { withDataLuminarySync } from 'js/src/luminary/data-luminary';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import {
    applyUserSceneDurationOperation,
    createOverlayOperation,
    deleteSceneGroupOperation,
    moveOverlaysAwayFromTimesOperation,
    replaceSlideOperation,
} from 'js/src/luminary/luminary-operations';
import {
    getPrePackagedMorphPosition,
    getPrePackagedSceneSequenceSceneGroupIds,
    getPrePackagedSceneSequenceSceneIds,
    getVoiceDrivenAsset,
    getVoiceDrivenAssetPlaybackRegions,
    getVoiceDrivenBaseAssetSourceDuration,
    isVoiceDrivenSceneGroup,
    translateSceneGroupTimeToTranscriptTime,
    translateTranscriptTimeToSceneGroupTime,
    translateVideoTimeToTranscriptTime,
} from 'js/src/luminary/luminary-selectors';
import { applyRecommendedSceneOverridesOperation } from 'js/src/luminary/swap-scene-operations';

export type SequenceMemberData = {
    sceneDesignId: string;
    serializedSceneGroupData: Record<string, unknown>;
    serializedSceneData: Record<string, unknown>;
    duration: number;
    durationProportion: number;
};

export type SceneSequenceDesign = {
    sceneSequenceId: string;
    videoId: number;
    sceneSequenceMembers: SequenceMemberData[];
    displayName: string | null;
    totalDuration: number;
};

export type SceneSequencePreviewData = {
    sceneSequenceId: string;
    previewSceneIds: string[];
    primaryPreviewSceneId: string;
    isRendered: boolean;
};

/**
 * Counts non-empty transcript words within a video time range for a voice-driven scene group.
 * Returns null for non-voice-driven scene groups or if the editor is unavailable.
 */
export const getWordCountInVideoTimeRangeOperation = createLuminaryOperation(
    (
        { luminary, getState },
        sceneGroupId: string,
        videoStartTime: number,
        videoEndTime: number,
    ): number | null => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup || !isVoiceDrivenSceneGroup(sceneGroup)) return null;

        const editor = getEditorFromSceneGroupId(getState(), sceneGroup.id);
        if (!editor) return null;

        const playbackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const sourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
        const transcriptStart = translateVideoTimeToTranscriptTime(
            videoStartTime,
            playbackRegions,
            sourceDuration,
            sceneGroup.startTime,
        );
        const transcriptEnd = translateVideoTimeToTranscriptTime(
            videoEndTime,
            playbackRegions,
            sourceDuration,
            sceneGroup.startTime,
            { includeTrailingCut: false },
        );

        return editor.getEditorState().read(() => {
            return $getWordNodesBetweenTimes(transcriptStart, transcriptEnd).length;
        });
    },
);

/**
 * Fetches and processes scene sequence designs for a template from the API.
 * Can be called directly from a Redux thunk (no luminary context required).
 */
export async function buildSceneSequenceDesigns(
    templateId: string,
    templateBundle: ReturnType<typeof getTemplateBundle>,
): Promise<SceneSequenceDesign[]> {
    const response = await ApiService.videosSceneSequencesList({
        query: { template: templateId },
    });
    if (!response.data?.results) return [];

    const chains: SceneSequenceDesign[] = [];

    for (const video of response.data.results) {
        const videoData = video.video_data;
        if (!videoData) continue;

        const chain = withDataLuminarySync(
            (dataLuminary) => {
                dataLuminary.load(videoData as OpaqueVideoData);

                const sceneGroups = dataLuminary.sceneGroups;
                if (sceneGroups.length < 2) return null;

                // Verify this is actually a scene sequence
                const firstScene = sceneGroups[0]?.scenes[0];
                if (!firstScene) return null;
                if (getPrePackagedMorphPosition(firstScene) !== 'start') return null;

                const totalDuration = sceneGroups.reduce((sum, sg) => sum + sg.duration, 0);
                if (totalDuration <= 0) return null;

                const sceneSequenceMembers: SequenceMemberData[] = sceneGroups.map((sg) => {
                    const scene = sg.scenes[0];
                    return {
                        sceneDesignId: scene.sceneDesign,
                        serializedSceneGroupData: sg.save(),
                        serializedSceneData: scene.save(),
                        duration: sg.duration,
                        durationProportion: sg.duration / totalDuration,
                    };
                });

                return {
                    sceneSequenceId: video.slug,
                    videoId: video.id,
                    sceneSequenceMembers,
                    displayName: video.name || null,
                    totalDuration,
                } satisfies SceneSequenceDesign;
            },
            { templateBundle: templateBundle ?? undefined },
        );

        if (chain) {
            chains.push(chain);
        }
    }

    return chains;
}

/**
 * Loads scene sequence designs into a local Luminary instance for preview,
 * creating scene groups for each chain member with content copied from
 * the source scene. Returns preview data for each chain.
 */
export const loadSceneSequencePreviewsIntoLocalLuminaryOperation = createLuminaryOperation(
    (
        { luminary, getState, call },
        localLuminary: Luminary,
        sceneSequenceDesigns: SceneSequenceDesign[],
        sourceSceneId: string,
        sourceSentenceToUseInstead: string | undefined,
        sceneAttributes: Record<SceneDesignId, SceneAttributesResponse> | null,
    ): SceneSequencePreviewData[] => {
        const sourceScene = luminary.getSceneById(sourceSceneId);
        if (!sourceScene) return [];

        const sceneSwapper = new SceneSwapper(sourceScene, sourceSentenceToUseInstead);
        const previews: SceneSequencePreviewData[] = [];

        localLuminary.batchRun(() => {
            for (const sceneSequenceDesign of sceneSequenceDesigns) {
                const previewSceneIds: string[] = [];

                sceneSequenceDesign.sceneSequenceMembers.forEach((member) => {
                    const { scene: previewScene } = localLuminary.addSceneGroup({
                        sceneDesignId: member.sceneDesignId,
                    });

                    previewScene.load(member.serializedSceneData);
                    sceneSwapper.copyContentToScene(previewScene, {});

                    call(
                        applyRecommendedSceneOverridesOperation,
                        previewScene.id,
                        sceneAttributes,
                        {
                            luminary: localLuminary,
                        },
                    );

                    previewScene.duration = member.duration;

                    previewSceneIds.push(previewScene.id);
                });

                previews.push({
                    sceneSequenceId: sceneSequenceDesign.sceneSequenceId,
                    previewSceneIds,
                    primaryPreviewSceneId: previewSceneIds[0],
                    isRendered: false,
                });
            }
        });

        return previews;
    },
);

/**
 * Swaps a scene (or existing scene sequence) to a new scene sequence design.
 * For text-on-media scenes: creates N separate scene groups with morph transitions.
 * For voice-driven scenes: creates N overlay scenes within the same scene group.
 */
export const swapToSceneSequenceOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        localLuminary: Luminary,
        chainDesign: SceneSequenceDesign,
        sourceSentenceToUseInstead: string | undefined,
        sceneAttributes: Record<SceneDesignId, SceneAttributesResponse> | null,
        faceDetections: BoundingBox[],
    ) => {
        const swapSceneDesignSlideId = getSwapSceneDesignSlideId(getState());
        if (!swapSceneDesignSlideId) {
            Toast.error('The scene was deleted or changed. Please try again.');
            return;
        }

        const sourceScene = luminary.getSceneById(swapSceneDesignSlideId);
        if (!sourceScene) return;

        const sourceSceneGroup = sourceScene.sceneGroup;
        const parentActionGroup = uuid();

        const morphPosition = getPrePackagedMorphPosition(sourceScene);
        const isSourceChainStart = morphPosition === 'start';

        const combinedDuration = (() => {
            if (!isSourceChainStart) return sourceSceneGroup.duration;
            const chainGroupIds = getPrePackagedSceneSequenceSceneGroupIds(
                luminary,
                sourceSceneGroup.id,
            );
            const total = chainGroupIds.reduce(
                (sum, id) => sum + luminary.getSceneGroupById(id).duration,
                0,
            );
            const secondaryIds = chainGroupIds.slice(1);
            for (const id of [...secondaryIds].reverse()) {
                call(deleteSceneGroupOperation, id, false, parentActionGroup);
            }
            return total;
        })();

        if (isVoiceDrivenSceneGroup(sourceSceneGroup)) {
            call(
                swapToSceneSequenceVoiceDrivenOperation,
                chainDesign,
                swapSceneDesignSlideId,
                sourceSentenceToUseInstead,
                sceneAttributes,
                faceDetections,
                parentActionGroup,
            );
        } else {
            call(
                swapToSceneSequenceTextOnMediaOperation,
                localLuminary,
                chainDesign,
                swapSceneDesignSlideId,
                combinedDuration,
                sourceSentenceToUseInstead,
                sceneAttributes,
                faceDetections,
                parentActionGroup,
            );
        }

        analytics.track('Scene design swapped to scene sequence', {
            sceneId: swapSceneDesignSlideId,
            sceneGroupId: sourceSceneGroup.id,
            sceneSequenceId: chainDesign.sceneSequenceId,
            sceneSequenceLength: chainDesign.sceneSequenceMembers.length,
            templateId: luminary.template.id,
        });

        dispatch(clearTrackedSlideTextAssets({ sceneId: swapSceneDesignSlideId }));
        call(checkpointVideoOperation, { systemTriggered: false, parentActionGroup });
    },
);

/**
 * Text-on-media swap: creates N separate scene groups with morph transitions,
 * splitting duration proportionally.
 */
const swapToSceneSequenceTextOnMediaOperation = createLuminaryOperation(
    (
        { luminary, call },
        localLuminary: Luminary,
        chainDesign: SceneSequenceDesign,
        sourceSceneId: string,
        combinedDuration: number,
        sourceSentenceToUseInstead: string | undefined,
        sceneAttributes: Record<SceneDesignId, SceneAttributesResponse> | null,
        faceDetections: BoundingBox[],
        parentActionGroup: string,
    ) => {
        const sourceScene = luminary.getSceneById(sourceSceneId);
        if (!sourceScene) return;

        const sourceSceneGroup = sourceScene.sceneGroup;
        const insertIndex = sourceSceneGroup.index;
        const sceneSwapper = new SceneSwapper(sourceScene, sourceSentenceToUseInstead);

        const newGroupIds = chainDesign.sceneSequenceMembers.map((member, i) => {
            const { scene: newScene } = localLuminary.addSceneGroup({
                sceneDesignId: member.sceneDesignId,
            });

            newScene.load(member.serializedSceneData);
            sceneSwapper.copyContentToScene(newScene, { maxSubscenesToCopy: 1 });

            call(applyRecommendedSceneOverridesOperation, newScene.id, sceneAttributes, {
                luminary: localLuminary,
            });

            if (i === 0) {
                call(replaceSlideOperation, sourceSceneId, newScene, [], false, parentActionGroup);
                return sourceSceneGroup.id;
            }
            const { scene: insertedScene, sceneGroup: insertedGroup } = luminary.addSceneGroup({
                sceneDesignId: member.sceneDesignId,
            });
            luminary.moveSceneGroupToIndex(insertedGroup.id, insertIndex + i);
            insertedScene.load(member.serializedSceneData);
            sceneSwapper.copyContentToScene(insertedScene, { maxSubscenesToCopy: 1 });
            call(applyRecommendedSceneOverridesOperation, insertedScene.id, sceneAttributes, {
                luminary,
            });
            return insertedScene.sceneGroup.id;
        });

        newGroupIds.forEach((groupId, i) => {
            const group = luminary.getSceneGroupById(groupId);
            const scene = group.scenes[0];
            if (!scene) return;

            const memberDuration =
                combinedDuration * chainDesign.sceneSequenceMembers[i].durationProportion;
            call(
                applyUserSceneDurationOperation,
                scene.id,
                memberDuration,
                true,
                parentActionGroup,
            );
        });
    },
);

/**
 * Voice-driven swap: creates N overlay scenes within the same scene group,
 * distributing transcript words proportionally.
 */
const swapToSceneSequenceVoiceDrivenOperation = createLuminaryOperation(
    (
        { luminary, call },
        chainDesign: SceneSequenceDesign,
        sourceSceneId: string,
        sourceSentenceToUseInstead: string | undefined,
        sceneAttributes: Record<SceneDesignId, SceneAttributesResponse> | null,
        faceDetections: BoundingBox[],
        parentActionGroup: string,
    ) => {
        const sourceScene = luminary.getSceneById(sourceSceneId);
        if (!sourceScene) return;

        const sceneGroup = sourceScene.sceneGroup;
        const voiceDrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const voiceDrivenAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);

        // If the source scene is part of an existing sequence, use the combined range
        // and remove secondary scenes first
        const sequenceSceneIds = getPrePackagedSceneSequenceSceneIds(sourceScene);
        const lastScene =
            sequenceSceneIds.length > 1
                ? luminary.getSceneById(sequenceSceneIds[sequenceSceneIds.length - 1])
                : null;
        const rangeEndVideoTime = lastScene ? lastScene.endTime : sourceScene.endTime;

        if (sequenceSceneIds.length > 1) {
            for (let i = sequenceSceneIds.length - 1; i >= 1; i--) {
                sceneGroup.removeScene(sequenceSceneIds[i]);
            }
        }

        const transcriptStartTime = translateVideoTimeToTranscriptTime(
            sourceScene.startTime,
            voiceDrivenAssetPlaybackRegions,
            voiceDrivenAssetSourceDuration,
            sceneGroup.startTime,
        );
        const transcriptEndTime = translateVideoTimeToTranscriptTime(
            rangeEndVideoTime,
            voiceDrivenAssetPlaybackRegions,
            voiceDrivenAssetSourceDuration,
            sceneGroup.startTime,
            { includeTrailingCut: false },
        );

        call(
            createSceneSequenceOverlayOperation,
            sceneGroup.id,
            transcriptStartTime,
            transcriptEndTime,
            chainDesign,
            false,
            parentActionGroup,
        );
    },
);

/**
 * Creates a scene sequence of overlay scenes within a voice-driven scene group.
 * Distributes transcript words proportionally across the chain members.
 */
export const createSceneSequenceOverlayOperation = createLuminaryOperation(
    (
        { luminary, call, getState },
        sceneGroupId: string,
        transcriptStartTime: number,
        transcriptEndTime: number,
        sceneSequenceDesign: SceneSequenceDesign,
        systemTriggered: boolean,
        parentActionGroup: string,
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) return;

        const voiceDrivenAsset = getVoiceDrivenAsset(sceneGroup);
        if (!voiceDrivenAsset) return;
        const editor = getEditorFromSceneGroupId(getState(), sceneGroupId);
        if (!editor) {
            return;
        }

        const voicedrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const voicedrivenAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);

        call(
            moveOverlaysAwayFromTimesOperation,
            sceneGroupId,
            transcriptStartTime,
            transcriptEndTime,
            [],
            systemTriggered,
            parentActionGroup,
        );

        const startTimeInSceneGroupTime = translateTranscriptTimeToSceneGroupTime(
            transcriptStartTime,
            voicedrivenAssetPlaybackRegions,
            voicedrivenAssetSourceDuration,
        );
        const endTimeInSceneGroupTime = translateTranscriptTimeToSceneGroupTime(
            transcriptEndTime,
            voicedrivenAssetPlaybackRegions,
            voicedrivenAssetSourceDuration,
        );
        const totalSceneSequenceDuration = endTimeInSceneGroupTime - startTimeInSceneGroupTime;
        const sceneMemberTargetDurations = sceneSequenceDesign.sceneSequenceMembers.map(
            (member) => {
                return {
                    member,
                    targetDuration: totalSceneSequenceDuration * member.durationProportion,
                };
            },
        );

        let startTime = startTimeInSceneGroupTime;
        editor.getEditorState().read(() => {
            sceneMemberTargetDurations.forEach((memberWithDuration, i) => {
                const overlayStart = translateSceneGroupTimeToTranscriptTime(
                    startTime,
                    voicedrivenAssetPlaybackRegions,
                    voicedrivenAssetSourceDuration,
                );
                const overlayEndTarget = translateSceneGroupTimeToTranscriptTime(
                    startTime + memberWithDuration.targetDuration,
                    voicedrivenAssetPlaybackRegions,
                    voicedrivenAssetSourceDuration,
                );
                const isLast = i === sceneMemberTargetDurations.length - 1;

                const overlayEnd = isLast
                    ? transcriptEndTime
                    : $getFineOverlayBoundaryTimeCloseTo(
                          overlayEndTarget,
                          voicedrivenAssetPlaybackRegions,
                          voicedrivenAssetSourceDuration,
                          {
                              getIdealOverlayBoundaryTime: true,
                          },
                      );

                const sceneId = call(
                    createOverlayOperation,
                    sceneGroupId,
                    overlayStart,
                    overlayEnd,
                    memberWithDuration.member.sceneDesignId,
                    systemTriggered,
                    parentActionGroup,
                );

                const scene = luminary.getSceneById(sceneId);
                if (scene) {
                    // load the data, but keep the original target duration
                    const originalDuration = scene.duration;
                    scene.load(memberWithDuration.member.serializedSceneData);
                    scene.duration = originalDuration;
                }

                startTime = translateTranscriptTimeToSceneGroupTime(
                    overlayEnd,
                    voicedrivenAssetPlaybackRegions,
                    voicedrivenAssetSourceDuration,
                );
            });
        });
    },
);
